import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, isDevMode } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ConvertHelper } from '@barthauer/abc/core';
import { Observable, Subscription } from 'rxjs';
import { MessageBoxComponent } from './messageBox.component';
import { DialogResult, MessageBoxButtons, MessageBoxIcon, MessageMode } from './messages.model';
import { MessageSettings } from './messageSettings';

@Injectable({
  providedIn: 'root',
})
export class MessageBoxService {
  constructor(public dialog: MatDialog) {}

  showMessageBox(settings: MessageSettings): Observable<DialogResult> {
    const dialogRef: MatDialogRef<MessageBoxComponent> = this.dialog.open(MessageBoxComponent, {
      panelClass: 'messageBox-dialog-container',
      backdropClass: 'messageBoxBackdrop',
      height: settings?.errorMessage ? '250px' : 'auto',
      width: '450px',
      disableClose: true,
      hasBackdrop: true,
      autoFocus: false,
      data: { settings: settings },
    });

    return dialogRef.afterClosed();
  }

  public closeAllBoxes(): void {
    this.dialog?.closeAll();
  }

  subscribeAndShowAnyError<T>(
    observable: Observable<T>,
    forceShowErrorDetails = false,
    next?: (value: T) => void,
    complete?: () => void,
    continueAfterMessageBox?: (err: any) => void
  ): Subscription {
    return observable.subscribe({
      next: next ?? ((v) => {}),
      error: (err) => {
        let message: string;
        let errorMessage: string;

        if (err) {
          if (err instanceof HttpErrorResponse) {
            message = err.message;

            if (err.error) {
              errorMessage = ConvertHelper.toString(err.error, 4);
            } else {
              errorMessage = 'No inner error provided.';
            }
          } else if (err instanceof Error) {
            message = err.message;

            if (err.stack) {
              errorMessage = err.stack;
            } else {
              errorMessage = 'No error stack provided.';
            }
          }
        } else {
          message = 'Ein unbekannter Fehler is aufgetreten!';
          errorMessage = null;
        }

        if (!forceShowErrorDetails && !isDevMode()) {
          errorMessage = null;
        }

        const settings: MessageSettings = {
          mode: MessageMode.Error,
          caption: 'Fehler!',
          button: MessageBoxButtons.OK,
          icon: MessageBoxIcon.Error,
          message: message,
          errorMessage: errorMessage,
        };

        const msgBoxSubscription: Subscription = this.showMessageBox(settings).subscribe({
          next: (dlgResult) => this.unsubscribeErrorMsgBoxAndContinue(msgBoxSubscription, err, continueAfterMessageBox),
          error: (_err) => this.unsubscribeErrorMsgBoxAndContinue(msgBoxSubscription, err, continueAfterMessageBox),
          complete: () => this.unsubscribeErrorMsgBoxAndContinue(msgBoxSubscription, err, continueAfterMessageBox),
        });
      },
      complete: complete ?? (() => {}),
    });
  }

  private unsubscribeErrorMsgBoxAndContinue(
    msgBoxSubscription: Subscription,
    err: any,
    continueAfterMessageBox?: (err: any) => void
  ): void {
    if (!msgBoxSubscription.closed) {
      msgBoxSubscription.unsubscribe();

      if (continueAfterMessageBox) {
        continueAfterMessageBox(err);
      }
    }
  }
}
